import { zodResolver } from '@hookform/resolvers/zod'
import { Loader, Pencil } from 'lucide-react'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'

import { MarketOrderDto, useAmendClientMarketOrder } from '@/gen/publicApi'

import { Button } from '@/components/button'
import {
    Dialog,
    DialogClose,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from '@/components/dialog'
import { Form } from '@/components/form'
import { toast } from '@/components/toast/useToast.ts'

import { randomStr } from '@/utils/random.ts'

import FormInput from '../form/formInput'
import IconButton from '../iconButton'

interface AmendOrderDialogProps {
    order: MarketOrderDto
}

const amendOrderFormSchema = z.object({
    price: z.string(),
    units: z.string(),
})

type AmendOrderFormValues = z.infer<typeof amendOrderFormSchema>

export default function OrderAmendDialog({ order }: AmendOrderDialogProps) {
    const [open, setOpen] = useState(false)

    const form = useForm<AmendOrderFormValues>({
        resolver: zodResolver(amendOrderFormSchema),
        defaultValues: {
            price: order.price,
            units: order.units,
        },
        mode: 'onChange',
    })

    const newClientOrderRef = randomStr(10)
    const amendOrderMutator = useAmendClientMarketOrder(
        order.clientCode,
        order.marketCode,
        order.clientOrderRef,
        newClientOrderRef,
        {
            mutation: {
                onSuccess: () => {
                    toast({
                        title: 'Successfully amended order',
                    })
                    setOpen(false)
                },
                onError: () => {
                    toast({
                        variant: 'destructive',
                        title: 'Error amended order',
                    })
                },
            },
        },
    )

    const onSubmit = (data: AmendOrderFormValues) => {
        toast({
            title: 'Amending order...',
        })

        amendOrderMutator.mutate({
            ...data,
            symbol: order.instrumentSymbol,
            side: order.side,
            targetStrategy: order.targetStrategy,
        })
    }

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
                <IconButton icon={Pencil} tooltip="Amend Order" className="mr-4" />
            </DialogTrigger>
            <DialogContent>
                <DialogHeader>
                    <DialogTitle>Amend Order</DialogTitle>
                </DialogHeader>
                <Form {...form}>
                    <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                        <FormInput
                            control={form.control}
                            name="price"
                            label="Price"
                            disabled={amendOrderMutator.isPending}
                        />
                        <FormInput
                            control={form.control}
                            name="units"
                            label="Units"
                            disabled={amendOrderMutator.isPending}
                        />

                        <DialogFooter>
                            <DialogClose asChild>
                                <Button type="button" variant="outline" disabled={amendOrderMutator.isPending}>
                                    Cancel
                                </Button>
                            </DialogClose>
                            <Button type="submit" disabled={amendOrderMutator.isPending}>
                                {amendOrderMutator.isPending && <Loader className="mr-2 h-4 w-4 animate-spin" />}
                                Amend
                            </Button>
                        </DialogFooter>
                    </form>
                </Form>
            </DialogContent>
        </Dialog>
    )
}
